// SEQ-180 E18: project performance table. Sortable with aria-sort, a text
// filter, column visibility toggle, and CSV/XLSX export that honours the
// active filters (both the page's date/project/status filters, already
// baked into `rows` by the time this component sees them, and the table's
// own text filter and column visibility). Row click deep links to the
// project's Status (P&L) tab (src/pages/ProjectDetail.tsx, "status" tab,
// read via ?tab=status).
//
// SEQ-180 legibility follow-up (15 September 2026). Four findings, all from a
// rendered audit rather than from reading this file.
//
//   THE DRILL-DOWN TARGETS WERE NOT LINKS. Twenty-eight `<span role="link">`
//   elements with a click handler and no href, so middle click and
//   open-in-new-tab did nothing, which is exactly what someone comparing two
//   projects reaches for. They are `<Link>` now, and they carry their own
//   focus ring rather than relying on the page's global one to survive a row
//   with its own hover tint.
//
//   THE EXPORTS CARRIED RAW FLOATS AND A UTC FILENAME. 45.61054215979711 where
//   the screen said 45.6%, and a name stamped with yesterday's date for every
//   Australian user before 10am. Both fixed in tableExport.ts, which is the one
//   place the CSV and the XLSX now agree.
//
//   NEITHER EXPORT SAID ANYTHING. A file written into a folder nobody is
//   looking at, with no confirmation, is indistinguishable from a button that
//   does nothing. Both now raise a toast naming the file and the row count.
//
//   NEGATIVE MONEY WAS BELOW AA. #ef4444 at 3.76:1 on a 13px figure. A chart
//   fill may sit at 3:1; a number may not.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { ArrowUpDown, ArrowUp, ArrowDown, Download, Settings2, Table2 } from "lucide-react";
import { metricsToRollup, PNL_ROLLUP_METRICS, projectRowsToRollups, type PnlRollup, type ProjectRollup } from "../../features/pnlAnalytics/rollup";
import { pnlChartCardStyle, formatPnlMetric } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import { PNL_METRIC_KEYS, type PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { RAW_WAGES_NOTE, showsRawWagesNote } from "../../lib/pnl/wagesMultiplierGrain";
import { isSafeModeActive } from "../../lib/calculations";
import { csvCell, xlsxCell } from "../../features/pnlAnalytics/spreadsheetSafety";
import { exportConfirmation, exportFilename, exportHeader, exportNumber } from "../../features/pnlAnalytics/tableExport";
import {
  buildProjectMonthTable,
  projectMonthExportRecords,
  sortProjectMonthRows,
  TOTAL_GROUP,
} from "../../features/pnlAnalytics/projectMonthTable";
import {
  BY_MONTH_COLUMNS_STORAGE_KEY,
  byMonthRequestPlan,
  BY_MONTH_WINDOW_STORAGE_KEY,
  canUntickColumn,
  effectiveMonthSort,
  grainRequestKey,
  isMonthDataCurrent,
  LAST_COLUMN_NOTE,
  MONTH_WINDOW_CHOICES,
  monthWindowLabel,
  parseSavedMonthWindow,
  serialiseMonthWindow,
  windowMonths,
  windowNarrowsRange,
  type MonthWindow,
  PROJECT_MONTH_RANGE_MESSAGES,
  projectMonthRangeResult,
  parseSavedColumnKeys,
  serialiseColumnKeys,
  type TableView,
} from "../../features/pnlAnalytics/projectMonthView";
import {
  ALL_PROJECTS_LABEL,
  applyCostsEntered,
  buildAllProjectsRow,
  COSTS_CHECK_FAILED_NOTE,
  TOTALS_COUNT_NO_COST_MONTHS_NOTE,
  costsTotalLabel,
  fullyUnfinishedMonths,
  noCostKeys,
  trustedCheck,
  loadShowNoCostMonths,
  saveShowNoCostMonths,
  SHOW_NO_COST_MONTHS_HINT,
  SHOW_NO_COST_MONTHS_LABEL,
} from "../../features/pnlAnalytics/projectMonthCosts";
import { useManualCostMonths } from "../../features/pnlAnalytics/useManualCostMonths";
import { useOnePnlAnalyticsGrain, type PnlAnalyticsQueryFilters } from "../../features/pnlAnalytics/usePnlAnalytics";
import { EMPTY_FIXTURE, usePnlProjectMonths } from "../../features/pnlAnalytics/usePnlProjectMonths";
import { useToast } from "../ui/ToastModal";
import { PnlChartHead } from "./PnlChartFrame";
import { PnlProjectMonthGrid, type MonthGridMessage } from "./PnlProjectMonthGrid";
import { HowWorkedOut } from "../howWorkedOut/HowWorkedOut";
import type { PnlMetricOption } from "../../features/pnlAnalytics/metricOption";
import { performanceColumnFigure } from "../../features/pnlAnalytics/pnlWidgetDescriptions";
import "./PnlWidgetCharts.css";

type TableRow = ProjectRollup & { projectName: string; status: string };

type ColumnKey =
  | "projectName"
  | "revenue"
  | "cost"
  | "profit"
  | "profitPercent"
  | "overtime"
  | "overtimePctRevenue"
  | "patching"
  | "patchingPctRevenue"
  | "patchingPctCost"
  // SEQ-182
  | "ordinaryHours"
  | "overtimeHours"
  | "totalLabourHours"
  | "overtimeHoursPct"
  // SEQ-182 round 3 (evidence-matrix gap): RPC-computed, per project row only.
  | "overtimePctWages"
  | "labourCostPerHour"
  | "revenuePerLabourHour"
  | "profitPerLabourHour";

interface ColumnDef {
  key: ColumnKey;
  label: string;
  /** SEQ-182: widened from "currency" | "percent" to the full PnlUnit union, plan section 6.5. */
  unit: "text" | PnlUnit;
  align: "left" | "right";
  /** SEQ-182 round 3. False hides the column from the default view; still available in the column visibility toggle. Undefined means visible by default. */
  defaultVisible?: boolean;
}

// SEQ-182: labels for the four new hours columns are built from their unit
// rather than hand-typed, so a decimals/unit change in the registry cannot
// silently drift out of step with the column header. Existing currency and
// percent columns below keep their original hand-written labels (already
// carrying their own unit marker, e.g. "Profit %"), left unchanged to avoid
// an unrelated visual diff on this pass.
function hoursColumnLabel(base: string): string {
  return base; // the word "Hours" already carries the unit; no "hrs" suffix needed
}

const COLUMNS: ColumnDef[] = [
  { key: "projectName", label: "Project", unit: "text", align: "left" },
  { key: "revenue", label: "Revenue", unit: "currency", align: "right" },
  { key: "cost", label: "Cost", unit: "currency", align: "right" },
  { key: "profit", label: "Profit", unit: "currency", align: "right" },
  { key: "profitPercent", label: "Profit %", unit: "percent", align: "right" },
  { key: "overtime", label: "Overtime", unit: "currency", align: "right" },
  { key: "overtimePctRevenue", label: "OT % revenue", unit: "percent", align: "right" },
  { key: "patching", label: "Patching", unit: "currency", align: "right" },
  { key: "patchingPctRevenue", label: "Patching %", unit: "percent", align: "right" },
  // AC05: patching must be shown as percent of revenue AND percent of total cost.
  { key: "patchingPctCost", label: "Patching % cost", unit: "percent", align: "right" },
  // SEQ-182: AC07/AC08, hours available through the analytics table (and both exports).
  { key: "ordinaryHours", label: hoursColumnLabel("Ordinary Hours"), unit: "hours", align: "right" },
  { key: "overtimeHours", label: hoursColumnLabel("Overtime Hours"), unit: "hours", align: "right" },
  { key: "totalLabourHours", label: hoursColumnLabel("Total Labour Hours"), unit: "hours", align: "right" },
  { key: "overtimeHoursPct", label: "Overtime Hours %", unit: "percent", align: "right" },
  // SEQ-182 round 3 (evidence-matrix gap): the RPC computes these and the
  // parity harness proves them, but nothing rendered them. Hidden by
  // default (a lot of dense columns already), available in the column
  // visibility toggle. currency_per_hour columns are masked under Safe
  // Mode (formatMetricValue delegates to formatCurrency); the percent one
  // is not.
  { key: "overtimePctWages", label: "OT % wages", unit: "percent", align: "right", defaultVisible: false },
  { key: "labourCostPerHour", label: "Labour cost / hr", unit: "currency_per_hour", align: "right", defaultVisible: false },
  { key: "revenuePerLabourHour", label: "Revenue / hr", unit: "currency_per_hour", align: "right", defaultVisible: false },
  { key: "profitPerLabourHour", label: "Profit / hr", unit: "currency_per_hour", align: "right", defaultVisible: false },
];

type SortDirection = "asc" | "desc";

/** The stem both exports share, so the CSV and the XLSX cannot end up named differently. */
const EXPORT_BASENAME = "pnl-analytics-projects";

/** The stem the By month exports share. */
const EXPORT_BASENAME_MONTHS = "pnl-analytics-project-months";

// The two metric keys whose month cells carry raw wages (see the note under the by month table).
const COLUMN_METRIC_KEY: Partial<Record<ColumnKey, string>> = {
  cost: PNL_METRIC_KEYS.calculated.totalCost,
  profit: PNL_METRIC_KEYS.calculated.netProfit,
};

interface PnlProjectPerformanceTableProps {
  rows: TableRow[];
  /**
   * SEQ-193. The page's live filters. When given, the table offers a
   * "Totals | By month" switch; without them (the editor preview) it is the
   * Totals table only.
   */
  queryFilters?: PnlAnalyticsQueryFilters;
  /** SEQ-240: the "How is this worked out" trigger beside the table's title. */
  info?: ReactNode;
  /** SEQ-240: the metric registry, so each column heading can explain its figure. Without it the headings carry no trigger. */
  metrics?: PnlMetricOption[];
  /** SEQ-240: dataset.meta.payrollAsAt, for the Last updated row of payroll columns. */
  payrollAsAt?: string | null;
}

function cellValue(row: TableRow, key: ColumnKey): string | number | null {
  return row[key];
}

// The one place the by month column set is saved. localStorage can be missing
// or throw (private window, blocked site data), so every access is guarded and
// the table works without it.
function loadByMonthColumns(allowed: readonly string[]): Set<ColumnKey> {
  let raw: string | null = null;
  try {
    raw = typeof localStorage === "undefined" ? null : localStorage.getItem(BY_MONTH_COLUMNS_STORAGE_KEY);
  } catch {
    raw = null;
  }
  return new Set(parseSavedColumnKeys(raw, allowed) as ColumnKey[]);
}

function saveByMonthColumns(keys: Set<ColumnKey>): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(BY_MONTH_COLUMNS_STORAGE_KEY, serialiseColumnKeys(keys));
  } catch {
    // Not saved: the choice still holds for this session.
  }
}

// SEQ-202: how many months By month shows, remembered per browser. Same
// guarded access as the column set above.
function loadMonthWindow(): MonthWindow {
  let raw: string | null = null;
  try {
    raw = typeof localStorage === "undefined" ? null : localStorage.getItem(BY_MONTH_WINDOW_STORAGE_KEY);
  } catch {
    raw = null;
  }
  return parseSavedMonthWindow(raw);
}

const MONTH_WINDOW_HINT = "Counts back from the end of the selected date range";

function saveMonthWindow(choice: MonthWindow): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(BY_MONTH_WINDOW_STORAGE_KEY, serialiseMonthWindow(choice));
  } catch {
    // Not saved: the choice still holds for this session.
  }
}

const METRIC_COLUMN_KEYS = COLUMNS.filter((c) => c.key !== "projectName").map((c) => c.key);

export function PnlProjectPerformanceTable({ rows, queryFilters, info, metrics, payrollAsAt }: PnlProjectPerformanceTableProps) {
  const { addToast } = useToast();
  // Default Totals: nothing changes, and nothing extra is fetched, for anyone
  // who never touches the switch.
  const [view, setView] = useState<TableView>("totals");
  // By month has its own sort and direction, so sorting one view never reorders the other.
  const [monthSort, setMonthSort] = useState<{ group: string; key: ColumnKey }>({ group: TOTAL_GROUP, key: "profit" });
  const [monthSortDirection, setMonthSortDirection] = useState<SortDirection>("desc");

  // The months the RPC will be asked for, and whether it is asked at all
  // (never while Totals shows). An open ended or over long range is never
  // fetched and the table says why.
  const rangeFrom = queryFilters?.from ?? null;
  const rangeTo = queryFilters?.to ?? null;
  const plan = useMemo(() => byMonthRequestPlan(view, rangeFrom, rangeTo), [view, rangeFrom, rangeTo]);
  const fullMonthList = plan.months;
  const monthsEnabled = plan.enabled;
  // SEQ-202: how many of those months to show. The request above still covers
  // the whole range, so changing the window never refetches the months.
  const [monthWindow, setMonthWindow] = useState<MonthWindow>(loadMonthWindow);
  const [showNoCostMonths, setShowNoCostMonths] = useState<boolean>(loadShowNoCostMonths);
  const stableFilters = useMemo<PnlAnalyticsQueryFilters>(
    () => queryFilters ?? { from: null, to: null, projectIds: [], statuses: [] },
    [queryFilters],
  );
  const monthData = usePnlProjectMonths(stableFilters, monthsEnabled);
  const monthResponse = monthData.data;
  const monthRowsCurrent = monthData.current;

  // SEQ-232: which project-months have income but no manual cost row. Read-only, see projectMonthCosts.ts.
  const rowsByProject = useMemo(() => {
    const byProject = new Map<string, Map<string, PnlRollup>>();
    if (!monthsEnabled || !monthResponse || !monthRowsCurrent) return byProject;
    for (const r of monthResponse.rows) {
      if (!r.projectId || !r.periodMonth) continue;
      let perMonth = byProject.get(r.projectId);
      if (!perMonth) {
        perMonth = new Map();
        byProject.set(r.projectId, perMonth);
      }
      perMonth.set(r.periodMonth, metricsToRollup(r.metrics));
    }
    return byProject;
  }, [monthsEnabled, monthResponse, monthRowsCurrent]);
  const costProjectIds = useMemo(() => [...rowsByProject.keys()], [rowsByProject]);
  const costCheck = useManualCostMonths(costProjectIds, fullMonthList, monthsEnabled && monthRowsCurrent);
  const flaggedKeys = useMemo(() => noCostKeys(rowsByProject, costCheck.check), [rowsByProject, costCheck.check]);
  const costCheckFailed =
    monthsEnabled && monthRowsCurrent && costCheck.settled && rowsByProject.size > 0 &&
    trustedCheck(rowsByProject, costCheck.check).status === "unavailable";

  // With the switch off, a month where every project is still waiting for costs is not a column at all,
  // and the Last N choice counts back over the months that remain.
  const availableMonths = useMemo(() => {
    if (fullMonthList === null) return null;
    if (showNoCostMonths || flaggedKeys.size === 0) return fullMonthList;
    const dropped = fullyUnfinishedMonths(fullMonthList, rowsByProject, flaggedKeys);
    return fullMonthList.filter((m) => !dropped.has(m));
  }, [fullMonthList, showNoCostMonths, flaggedKeys, rowsByProject]);
  const monthList = useMemo(
    () => (availableMonths === null ? null : windowMonths(availableMonths, monthWindow)),
    [availableMonths, monthWindow],
  );
  const windowNarrowed = availableMonths !== null && windowNarrowsRange(availableMonths, monthWindow);
  // The Total is read again only when its START moves. Unfinished months at the end are taken back out of it
  // (applyCostsEntered), so leaving them off needs no second call.
  const totalStartsLater =
    fullMonthList !== null && monthList !== null && monthList.length > 0 && monthList[0] !== fullMonthList[0];
  // The Total must cover only the months shown. It cannot be summed from the
  // month cells (cost and profit months carry raw wages, the project grain
  // carries the multiplied figure, see projectMonthTable.ts), so a Total that
  // starts later asks the project grain again from the first month shown.
  // "All" makes no extra call: its Total is the Totals view's own.
  const windowFilters = useMemo<PnlAnalyticsQueryFilters>(
    () => ({ ...stableFilters, from: monthList?.[0] ?? stableFilters.from }),
    [stableFilters, monthList],
  );
  // Held until the cost check settles, so the Total is not asked for twice when the dropped months move its start.
  const windowTotalsEnabled = monthsEnabled && totalStartsLater && costCheck.settled;
  const windowTotals = useOnePnlAnalyticsGrain(windowFilters, "project", PNL_ROLLUP_METRICS, EMPTY_FIXTURE, windowTotalsEnabled);
  const windowTotalsCurrent =
    !windowTotalsEnabled ||
    (windowTotals.data !== null &&
      isMonthDataCurrent(windowTotals.dataKey, grainRequestKey(windowFilters, "project", PNL_ROLLUP_METRICS)));
  const windowTotalByProject = useMemo(() => {
    if (!windowTotalsEnabled || !windowTotals.data) return null;
    return new Map(projectRowsToRollups(windowTotals.data.rows).map((r) => [r.projectId, r]));
  }, [windowTotalsEnabled, windowTotals.data]);
  // Recomputed when the view or the range changes, so a page left open over
  // midnight or a month boundary does not keep yesterday's "month to date".
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the inputs ARE the triggers; the clock is deliberately read inside
  const todayIso = useMemo(() => format(new Date(), "yyyy-MM-dd"), [view, rangeTo]);
  // Read the same way formatCurrency does (round 2 code review): a plain
  // check of the module-level flag, safe here because this component is
  // mounted below SafeModeProvider, which re-renders its whole subtree on
  // toggle. Exports are disabled outright rather than writing masked
  // strings into a downloaded file, since a file is not a screen share and
  // survives after Safe Mode is switched back off.
  const safeModeActive = isSafeModeActive();
  const [textFilter, setTextFilter] = useState("");
  const [sortBy, setSortBy] = useState<ColumnKey>("profit");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  // Two column sets, one per view. Totals keeps its own (never shrunk by the
  // by month default); By month opens on Revenue, Cost, Profit and Profit % and
  // remembers the user's change under its own storage key.
  const [totalsColumns, setTotalsColumns] = useState<Set<ColumnKey>>(
    new Set(COLUMNS.filter((c) => c.defaultVisible !== false).map((c) => c.key)),
  );
  const [monthColumns, setMonthColumns] = useState<Set<ColumnKey>>(() => loadByMonthColumns(METRIC_COLUMN_KEYS));
  const visibleColumns = view === "months" ? new Set<ColumnKey>(["projectName", ...monthColumns]) : totalsColumns;
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  const filtered = useMemo(() => {
    const query = textFilter.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) => r.projectName.toLowerCase().includes(query));
  }, [rows, textFilter]);

  const sorted = useMemo(() => {
    const withNullsLast = [...filtered].sort((a, b) => {
      const av = cellValue(a, sortBy);
      const bv = cellValue(b, sortBy);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (typeof av === "string" || typeof bv === "string") {
        return String(av).localeCompare(String(bv));
      }
      return (av as number) - (bv as number);
    });
    if (sortDirection === "desc") withNullsLast.reverse();
    return withNullsLast;
  }, [filtered, sortBy, sortDirection]);

  const columns = COLUMNS.filter((c) => visibleColumns.has(c.key));
  // The metric columns shown under every month and under Total (the picker
  // controls all of them at once). The project name is a row header here.
  const metricColumns = columns.filter((c) => c.key !== "projectName");

  // Rows fetched for other filters are never drawn under the new month
  // columns: until `current`, the table shows its loading state.
  const freshBuilt = useMemo(() => {
    if (!monthsEnabled || monthList === null || monthList.length === 0 || !monthResponse || !monthRowsCurrent) return null;
    if (!costCheck.settled) return null;
    if (windowTotalsEnabled && (!windowTotalsCurrent || !windowTotalByProject)) return null;
    const built = buildProjectMonthTable(
      monthList,
      monthResponse.rows,
      filtered.map((r) => ({
        projectId: r.projectId,
        projectName: r.projectName,
        status: r.status,
        // A project with no data in the shorter span has no Total there: blank, never $0.
        total: windowTotalByProject ? (windowTotalByProject.get(r.projectId) ?? null) : r,
      })),
      rangeTo ? { rangeToIso: rangeTo, todayIso } : undefined,
    );
    return applyCostsEntered(built, flaggedKeys, showNoCostMonths, monthList[0]);
  }, [monthsEnabled, monthList, monthResponse, monthRowsCurrent, filtered, rangeTo, todayIso, windowTotalsEnabled, windowTotalsCurrent, windowTotalByProject, costCheck.settled, flaggedKeys, showNoCostMonths]);
  const freshTable = freshBuilt ? freshBuilt.table : null;
  const totalLabel = costsTotalLabel(monthList?.length ?? 0, windowNarrowed, freshBuilt?.leftOutOfTotal ?? false);

  // Changing the window must not blank the grid. While the Total is read again
  // for the new span, the last table for the SAME filters stays on screen,
  // marked busy, with its Total column dimmed: no layout shift, and the scroll
  // position holds. A table held for other filters is never shown.
  const filtersKey = JSON.stringify(stableFilters);
  const [held, setHeld] = useState<{ table: NonNullable<typeof freshTable>; label: string; filtersKey: string } | null>(null);
  useEffect(() => {
    if (freshTable) setHeld({ table: freshTable, label: totalLabel, filtersKey });
  }, [freshTable, totalLabel, filtersKey]);
  const windowTotalsPending =
    monthsEnabled && monthList !== null && !!monthResponse && monthRowsCurrent && windowTotalsEnabled && !windowTotalsCurrent && !windowTotals.error;
  const busy = freshTable === null && windowTotalsPending && held !== null && held.filtersKey === filtersKey;
  const monthTable = freshTable ?? (busy ? held.table : null);
  const gridTotalLabel = freshTable ? totalLabel : busy ? held.label : totalLabel;

  // The sort is corrected when its column is no longer shown (the user
  // unticked Profit), so a visible header always carries aria-sort.
  const visibleMetricKeys = metricColumns.map((c) => c.key).join(",");
  const activeMonthSort = useMemo(
    () =>
      effectiveMonthSort(
        monthSort,
        visibleMetricKeys ? visibleMetricKeys.split(",") : [],
        monthTable ? monthTable.columns.map((c) => c.periodMonth) : undefined,
      ),
    [monthSort, visibleMetricKeys, monthTable],
  );
  const sortedMonthRows = useMemo(
    () => (monthTable ? sortProjectMonthRows(monthTable.rows, activeMonthSort.group, activeMonthSort.key, monthSortDirection) : []),
    [monthTable, activeMonthSort, monthSortDirection],
  );

  // SEQ-232: pinned first, never sorted. Built from the rows the table shows, so the text filter narrows it too.
  const allProjectsRow = useMemo(
    () => (monthTable && monthTable.rows.length > 0 ? buildAllProjectsRow(monthTable, monthTable.rows, textFilter.trim() ? `${ALL_PROJECTS_LABEL} (filtered)` : ALL_PROJECTS_LABEL) : null),
    [monthTable, textFilter],
  );
  // True only when something on screen was actually left out: a dropped column or a blanked cell.
  const firstShown = monthList?.[0] ?? "";
  const someLeftOut =
    !showNoCostMonths &&
    (!!freshBuilt?.leftOutOfTotal ||
      (availableMonths !== null && fullMonthList !== null && fullMonthList.some((m) => m >= firstShown && !availableMonths.includes(m))) ||
      (monthTable?.rows.some((r) => (monthList ?? []).some((m) => r.excluded?.has(m))) ?? false));

  const toggleMonthSort = (group: string, key: string) => {
    if (activeMonthSort.group === group && activeMonthSort.key === key) {
      setMonthSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setMonthSort({ group, key: key as ColumnKey });
      setMonthSortDirection("desc");
    }
  };

  // Cost and profit months carry RAW wages and the Total carries the multiplied
  // figure, so they do not add up. Say so whenever either column is on screen.
  const showWagesNote =
    view === "months" && metricColumns.some((c) => showsRawWagesNote("project_month", COLUMN_METRIC_KEY[c.key] ?? ""));

  const toggleSort = (key: ColumnKey) => {
    if (sortBy === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection("desc");
    }
  };

  // The picker edits the set for the view that is showing, and nothing else.
  const toggleColumn = (key: ColumnKey) => {
    if (key === "projectName") return; // project name always stays visible
    const apply = (prev: Set<ColumnKey>) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    };
    if (view === "months") {
      const next = apply(monthColumns);
      setMonthColumns(next);
      saveByMonthColumns(next);
    } else {
      setTotalsColumns(apply);
    }
  };

  const displayValue = (row: TableRow, col: ColumnDef): string => {
    if (col.unit === "text") return row.projectName;
    const value = cellValue(row, col.key) as number | null;
    // SEQ-182: hours (and any future non-currency/percent unit) go straight
    // through formatMetricValue, the one function every P&L surface shares
    // (plan section 6.2), so "Do not prepend $ to non-financial values"
    // holds here exactly as it does on the tab band.
    if (col.unit === "percent" || col.unit === "currency") {
      return formatPnlMetric(value, col.unit);
    }
    return formatMetricValue(value, col.unit);
  };

  // The figures leave at the precision the screen shows them at, and they leave
  // as NUMBERS. Rounding to text would make the file unsortable and unsummable,
  // which is the only reason anyone exports it (tableExport.ts). There is no
  // month on month change anywhere in this view (SEQ-202), so the file is
  // values only, and it holds the months shown, the same as the screen.
  const exportRows = () => {
    if (view === "months") {
      if (!monthTable) return [];
      return projectMonthExportRecords(
        monthTable,
        sortedMonthRows,
        metricColumns.map((c) => ({ key: c.key, label: c.label, unit: c.unit as PnlUnit })),
        "Project",
        gridTotalLabel,
        allProjectsRow,
      );
    }
    return sorted.map((row) => {
      const record: Record<string, string | number> = {};
      for (const col of columns) {
        record[exportHeader(col.label, col.unit)] =
          col.unit === "text" ? row.projectName : exportNumber(cellValue(row, col.key) as number | null, col.unit);
      }
      return record;
    });
  };
  const exportBasename = view === "months" ? EXPORT_BASENAME_MONTHS : EXPORT_BASENAME;

  // SEQ-180 phase 2 (security audit, LOW): quoting for commas is not enough.
  // A project name beginning with "=", "+", "-" or "@" is a live FORMULA to
  // every spreadsheet, so both exports run their strings through
  // features/pnlAnalytics/spreadsheetSafety.ts first. Numbers are untouched
  // and still sort and sum as numbers.
  const exportToCsv = () => {
    const data = exportRows();
    if (data.length === 0) return;
    const headers = Object.keys(data[0]);
    const lines = [
      headers.map((header) => csvCell(header)).join(","),
      ...data.map((row) => headers.map((h) => csvCell(row[h] ?? "")).join(",")),
    ];
    const filename = exportFilename(exportBasename, "csv", new Date());
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    addToast({ type: "success", title: "CSV downloaded", message: exportConfirmation(filename, data.length) });
  };

  const exportToXlsx = () => {
    const data = exportRows().map((row) => {
      const safe: Record<string, string | number> = {};
      for (const [key, value] of Object.entries(row)) safe[key] = xlsxCell(value);
      return safe;
    });
    if (data.length === 0) return;
    const filename = exportFilename(exportBasename, "xlsx", new Date());
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, view === "months" ? "Project performance by month" : "Project performance");
    XLSX.writeFile(wb, filename);
    addToast({ type: "success", title: "Spreadsheet downloaded", message: exportConfirmation(filename, data.length) });
  };

  // What stands in for the grid when there is nothing to draw yet. The same
  // body height as the Totals view, so switching views does not make the page
  // jump.
  let monthsMessage: MonthGridMessage | null = null;
  if (monthList !== null && monthList.length === 0) {
    monthsMessage = {
      text: "Every month in this range is still waiting for costs to be entered. Turn on Show months without costs to see them.",
      alert: false,
      retry: false,
    };
  } else if (monthList === null) {
    monthsMessage = {
      text: PROJECT_MONTH_RANGE_MESSAGES[projectMonthRangeResult(rangeFrom, rangeTo).issue ?? "open"],
      alert: false,
      retry: false,
    };
  } else if (monthData.error || (windowTotalsEnabled && windowTotals.error)) {
    monthsMessage = {
      text: `The months could not load. ${monthData.error ?? windowTotals.error}`,
      alert: true,
      retry: true,
    };
  } else if (!monthTable) {
    monthsMessage = { text: "Loading the months…", alert: false, retry: false };
  } else if (metricColumns.length === 0) {
    monthsMessage = { text: "Choose at least one column from the column menu.", alert: false, retry: false };
  } else if (monthTable.rows.length === 0) {
    monthsMessage = { text: "No projects match this filter.", alert: false, retry: false };
  }

  return (
    <div style={pnlChartCardStyle} className="pnl-chart-card">
      <PnlChartHead
        icon={<Table2 size={18} aria-hidden="true" style={{ color: "var(--seq-blue)" }} />}
        title="Project performance"
        info={info}
        caption={
          view === "months" ? (
            <>
              <span className="pnl-cap-long">
                One row per project, a column group for each month, then the total. Select a heading to sort, or a project name to open it.
              </span>
              <span className="pnl-cap-short">One row per project and month, then the total.</span>
            </>
          ) : (
            <>
              <span className="pnl-cap-long">One row per project, over the dates you pick. Select a heading to sort, or a project name to open it.</span>
              <span className="pnl-cap-short">One row per project, over the dates you pick. Select a heading to sort.</span>
            </>
          )
        }
      >
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-start", gap: 8 }}>
          {queryFilters && (
            <div className="pnl-view-switch" role="group" aria-label="Project performance view">
              <button type="button" aria-pressed={view === "totals"} onClick={() => setView("totals")}>
                Totals
              </button>
              <button type="button" aria-pressed={view === "months"} onClick={() => setView("months")}>
                By month
              </button>
            </div>
          )}
          {queryFilters && view === "months" && (
            <div className="pnl-view-switch" role="group" aria-label="Months shown" title={MONTH_WINDOW_HINT}>
              {MONTH_WINDOW_CHOICES.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  aria-pressed={monthWindow === choice}
                  onClick={() => {
                    setMonthWindow(choice);
                    saveMonthWindow(choice);
                  }}
                >
                  {monthWindowLabel(choice)}
                </button>
              ))}
            </div>
          )}
          {queryFilters && view === "months" && (
            <div className="pnl-view-switch" role="group" aria-label="Months without costs" title={SHOW_NO_COST_MONTHS_HINT}>
              <button
                type="button"
                aria-pressed={showNoCostMonths}
                onClick={() => {
                  const next = !showNoCostMonths;
                  setShowNoCostMonths(next);
                  saveShowNoCostMonths(next);
                }}
              >
                {SHOW_NO_COST_MONTHS_LABEL}
              </button>
            </div>
          )}
          <input
            id="pnl-table-filter"
            type="text"
            className="form-input"
            aria-label="Filter projects by name"
            placeholder="Filter by project name…"
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            style={{ padding: "6px 10px", fontSize: 12, flex: "1 1 150px", minWidth: 120, maxWidth: 240 }}
          />
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              aria-haspopup="true"
              aria-expanded={columnMenuOpen}
              onClick={() => setColumnMenuOpen((o) => !o)}
              title="Choose visible columns"
            >
              <Settings2 size={14} />
            </button>
            {columnMenuOpen && (
              <div
                style={{
                  position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 30,
                  background: "var(--bg-secondary)", border: "1px solid var(--border-color)",
                  borderRadius: 10, boxShadow: "var(--shadow-lg)", padding: 8, minWidth: 180,
                }}
              >
                <p style={{ margin: "2px 6px 6px", fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>
                  {view === "months" ? "Columns: By month" : "Columns: Totals"}
                </p>
                {COLUMNS.filter((c) => c.key !== "projectName").map((col) => (
                  <label key={col.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", fontSize: 12, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(col.key)}
                      disabled={!canUntickColumn(metricColumns.length, visibleColumns.has(col.key))}
                      onChange={() => toggleColumn(col.key)}
                      style={{ margin: 0, accentColor: "var(--seq-blue)" }}
                    />
                    {col.label}
                  </label>
                ))}
                {metricColumns.length <= 1 && (
                  <p style={{ margin: "6px 6px 2px", fontSize: 12, color: "var(--text-muted)" }}>{LAST_COLUMN_NOTE}</p>
                )}
              </div>
            )}
          </div>
          <span className="pnl-export-pair">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={exportToCsv}
              disabled={safeModeActive || busy}
              title={safeModeActive ? "Exports are off while Safe Mode is on" : "Export CSV"}
            >
              <Download size={14} /> CSV
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={exportToXlsx}
              disabled={safeModeActive || busy}
              title={safeModeActive ? "Exports are off while Safe Mode is on" : "Export XLSX"}
            >
              <Download size={14} aria-hidden="true" /> XLSX
            </button>
          </span>
        </div>
      </PnlChartHead>

      {view === "months" ? (
        <div className="pnl-month-body">
          {/* Always in the page so a screen reader hears it appear; empty unless the Total is being read again. */}
          <p role="status" className="pnl-visually-hidden">
            {busy ? `Updating the total for the last ${monthList?.length ?? 0} months…` : ""}
          </p>
          <div className="pnl-table-scroll pnl-table-scroll--months pnl-chart-body" style={{ overflowX: "auto" }} aria-busy={busy}>
            <PnlProjectMonthGrid
              table={monthsMessage ? null : monthTable}
              message={monthsMessage}
              onRetry={() => {
                monthData.refresh();
                windowTotals.refresh();
              }}
              metrics={metricColumns.map((c) => ({ key: c.key, label: c.label, unit: c.unit as PnlUnit }))}
              rows={sortedMonthRows}
              sort={activeMonthSort}
              sortDirection={monthSortDirection}
              onSort={toggleMonthSort}
              totalLabel={gridTotalLabel}
              allProjectsRow={monthsMessage ? null : allProjectsRow}
              columnInfo={
                metrics
                  ? (column) => (
                      <HowWorkedOut
                        definition={performanceColumnFigure(column.key, column.label, "months", metrics, { payrollAsAt })}
                        where="in the By month table"
                      />
                    )
                  : undefined
              }
              projectInfo={
                metrics ? (
                  <HowWorkedOut
                    definition={performanceColumnFigure("projectName", "Project", "months", metrics, { payrollAsAt })}
                    where="in the By month table"
                  />
                ) : undefined
              }
            />
          </div>
          {costCheckFailed && (
            <p role="status" className="pnl-month-note">
              {COSTS_CHECK_FAILED_NOTE}. Every month is shown as it was.
            </p>
          )}
          {someLeftOut && !monthsMessage && (
            <p className="pnl-month-note">
              Project months with income but no costs entered are left out of the figures, the All projects row and the Total.
              Turn on Show months without costs to include them.
            </p>
          )}
          {showWagesNote && <p className="pnl-month-note">{RAW_WAGES_NOTE}</p>}
        </div>
      ) : (
      <div className="pnl-table-scroll pnl-chart-body pnl-month-body" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {columns.map((col) => {
                const isSorted = sortBy === col.key;
                const ariaSort = isSorted ? (sortDirection === "asc" ? "ascending" : "descending") : "none";
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={ariaSort}
                    className={col.key === "projectName" ? "pnl-sticky-col" : undefined}
                    style={{
                      textAlign: col.align, padding: "8px 10px", fontSize: 12, fontWeight: 700,
                      color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.02em",
                      borderBottom: "2px solid var(--border-color)", whiteSpace: "nowrap",
                    }}
                  >
                    {/* A 19px control on a table people sort constantly. The
                        styling moved to .pnl-table-sort so a coarse pointer can
                        be given a 44px target without every sort header
                        carrying its own media query. */}
                    <span className="hwo-heading">
                      <button
                        type="button"
                        className="pnl-table-sort"
                        onClick={() => toggleSort(col.key)}
                        style={{ flexDirection: col.align === "right" ? "row-reverse" : "row" }}
                      >
                        {col.label}
                        {isSorted ? (
                          sortDirection === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                        ) : (
                          <ArrowUpDown size={12} style={{ opacity: 0.4 }} />
                        )}
                      </button>
                      {metrics && (
                        <HowWorkedOut
                          definition={performanceColumnFigure(col.key, col.label, "totals", metrics, { payrollAsAt })}
                          where="in the Project performance table"
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const projectPath = `/projects/${row.projectId}?tab=status`;
              return (
                <tr
                  key={row.projectId}
                  style={{ borderBottom: "1px solid var(--border-color-light)" }}
                  className="pnl-table-row"
                >
                  {columns.map((col) => {
                    const isNegativeMoney =
                      (col.unit === "currency" || col.unit === "currency_per_hour") &&
                      typeof cellValue(row, col.key) === "number" &&
                      (cellValue(row, col.key) as number) < 0;
                    return (
                      <td
                        key={col.key}
                        data-seq-private={col.key === "projectName" ? undefined : ""}
                        className={[
                          col.key === "projectName" ? "pnl-sticky-col" : "",
                          isNegativeMoney ? "pnl-table-negative" : "",
                        ].filter(Boolean).join(" ") || undefined}
                        style={{
                          padding: "8px 10px", textAlign: col.align,
                          fontWeight: col.key === "projectName" ? 600 : 400,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.key === "projectName" ? (
                          // A real anchor. The whole row used to be the click
                          // target with the name as a span carrying role="link",
                          // which gave twenty-eight keyboard stops that could
                          // not be middle-clicked, opened in a new tab or
                          // copied. Dropping the row handler costs a mouse
                          // convenience and buys the behaviour people actually
                          // reach for when comparing two projects.
                          <Link className="pnl-table-link" to={projectPath} title={row.projectName}>
                            {displayValue(row, col)}
                          </Link>
                        ) : (
                          displayValue(row, col)
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ padding: 20, textAlign: "center", color: "var(--text-muted)" }}>
                  No projects match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <p className="pnl-month-note">{TOTALS_COUNT_NO_COST_MONTHS_NOTE}</p>
      </div>
      )}
    </div>
  );
}
